import { HttpParams } from '@angular/common/http';
import { CreateFactDTO, Fact, UpdateFactDTO } from 'services/fact/fact.model';
import {
	CreateLegalRequestDTO,
	LegalRequest,
	UpdateLegalRequestDTO,
} from 'services/legal-request/legal-request.model';
import { CreateTagDTO, Tag, UpdateTagDTO } from 'services/tag/tag.model';
import { CreateThesisDTO, Thesis, UpdateThesisDTO } from 'services/thesis/thesis.model';
import { CreateTopicDTO, Topic, UpdateTopicDTO } from 'services/topic/topic.model';
import { AuditUser } from 'services/user/user.model';
import {
	MockHttpError,
	MockRequest,
	MockRoute,
	mockRoute,
	newMockId,
	nowIso,
	paginate,
} from '../mock-api';
import { buildCurrentUser } from '../session/session.mock';
import { MOCK_PEOPLE, MOCK_VERTICAL_INFO } from '../shared/mock-context.const';
import {
	KNOWLEDGE_BASE_PRODUCT_SETUP_ID,
	KNOWLEDGE_BASE_VERTICAL,
	MOCK_FACTS,
	MOCK_LEGAL_REQUESTS,
	MOCK_TAGS,
	MOCK_THESES,
	MOCK_THESIS_TOPIC_IDS,
	MOCK_TOPICS,
	slugify,
	toThesisReference,
} from './knowledge-base.const';

/**
 * Base de conhecimento da Contestação (tags, fatos, pedidos, teses e tópicos).
 *
 * Escopo das listagens, como no backend:
 * - `global=true` → só os globais (visão admin);
 * - `includeGlobal=false` → só os do produto do gestor;
 * - sem parâmetro → os do produto + os globais.
 *
 * O estado fica em memória: o CRUD persiste enquanto a página não for recarregada.
 * As referências (tags de um fato, fatos/pedidos de uma tese…) são guardadas como
 * cópias, mas sempre reidratadas a partir do estado atual na resposta.
 */

interface ScopedEntity {
	id: string;
	name: string;
	productSetupId?: string;
	createdAt: string;
	updatedAt: string;
	createdBy: AuditUser;
	updatedBy: AuditUser;
}

const state = {
	tags: structuredClone(MOCK_TAGS),
	facts: structuredClone(MOCK_FACTS),
	legalRequests: structuredClone(MOCK_LEGAL_REQUESTS),
	theses: structuredClone(MOCK_THESES),
	topics: structuredClone(MOCK_TOPICS),
	thesisTopicIds: { ...MOCK_THESIS_TOPIC_IDS } as Record<string, string>,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function currentAuditUser(): AuditUser {
	try {
		const user = buildCurrentUser();
		return { id: user.id, name: user.name, email: user.email, image: user.image };
	} catch {
		return MOCK_PEOPLE.gerente;
	}
}

function normalize(value: string): string {
	return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

function isGlobalEntity(entity: { productSetupId?: string }): boolean {
	return !entity.productSetupId;
}

function matchesScope(entity: { productSetupId?: string }, query: HttpParams): boolean {
	if (query.get('global') === 'true') return isGlobalEntity(entity);
	if (query.get('includeGlobal') === 'false') {
		return entity.productSetupId === KNOWLEDGE_BASE_PRODUCT_SETUP_ID;
	}
	return isGlobalEntity(entity) || entity.productSetupId === KNOWLEDGE_BASE_PRODUCT_SETUP_ID;
}

function matchesTerm(values: (string | undefined)[], term: string | null): boolean {
	if (!term?.trim()) return true;
	const normalizedTerm = normalize(term);
	return values.some((value) => value !== undefined && normalize(value).includes(normalizedTerm));
}

function matchesTags(entity: { tags: Tag[] }, query: HttpParams): boolean {
	const tagIds = query.getAll('tagIds') ?? [];
	if (tagIds.length === 0) return true;
	return entity.tags.some((tag) => tagIds.includes(tag.id));
}

function compareValues(first: unknown, second: unknown): number {
	if (typeof first === 'number' && typeof second === 'number') return first - second;
	if (typeof first === 'boolean' && typeof second === 'boolean')
		return Number(first) - Number(second);
	return String(first ?? '').localeCompare(String(second ?? ''), 'pt-BR', { sensitivity: 'base' });
}

/** Ordena por `sortBy`/`sortOrder`; sem parâmetro, os mais recentes primeiro */
function sortEntities<T extends ScopedEntity>(items: T[], query: HttpParams): T[] {
	const sortBy = query.get('sortBy') ?? 'createdAt';
	const direction =
		query.get('sortOrder') === 'asc' || (!query.get('sortOrder') && sortBy === 'name') ? 1 : -1;
	return [...items].sort((first, second) => {
		const firstValue =
			sortBy === 'createdBy' || sortBy === 'updatedBy'
				? first[sortBy].name
				: (first as Record<string, unknown>)[sortBy];
		const secondValue =
			sortBy === 'createdBy' || sortBy === 'updatedBy'
				? second[sortBy].name
				: (second as Record<string, unknown>)[sortBy];
		return compareValues(firstValue, secondValue) * direction;
	});
}

function findOrFail<T extends { id: string }>(items: T[], id: string, notFoundTitle: string): T {
	const item = items.find((candidate) => candidate.id === id);
	if (!item)
		throw new MockHttpError(404, notFoundTitle, `Nenhum registro encontrado com o id ${id}.`);
	return item;
}

/** Admin só altera globais; gestor só altera os do próprio produto */
function assertEditable(entity: ScopedEntity, isAdmin: boolean, noun: string) {
	if (isAdmin && !isGlobalEntity(entity)) {
		throw new MockHttpError(
			403,
			'Operação não permitida',
			`Somente o gestor do produto pode alterar ${noun}.`,
		);
	}
	if (!isAdmin && isGlobalEntity(entity)) {
		throw new MockHttpError(
			403,
			'Operação não permitida',
			`${noun.charAt(0).toUpperCase()}${noun.slice(1)} é global e só pode ser alterado(a) pelo administrador.`,
		);
	}
}

function assertUniqueName<T extends ScopedEntity>(
	items: T[],
	name: string,
	isAdmin: boolean,
	title: string,
	ignoreId?: string,
) {
	const normalizedName = normalize(name);
	const duplicate = items.some(
		(item) =>
			item.id !== ignoreId &&
			normalize(item.name) === normalizedName &&
			(isAdmin ? isGlobalEntity(item) : item.productSetupId === KNOWLEDGE_BASE_PRODUCT_SETUP_ID),
	);
	if (duplicate) throw new MockHttpError(409, title, `Já existe um registro com o nome "${name}".`);
}

function uniqueSlug(
	base: string,
	items: { id: string; slug: string }[],
	ignoreId?: string,
): string {
	const baseSlug = slugify(base) || 'item';
	let slug = baseSlug;
	let suffix = 2;
	while (items.some((item) => item.id !== ignoreId && item.slug === slug)) {
		slug = `${baseSlug}-${suffix++}`;
	}
	return slug;
}

function creationFields(isAdmin: boolean) {
	const user = currentAuditUser();
	const timestamp = nowIso();
	return {
		...(isAdmin ? {} : { productSetupId: KNOWLEDGE_BASE_PRODUCT_SETUP_ID }),
		createdAt: timestamp,
		updatedAt: timestamp,
		createdBy: user,
		updatedBy: user,
	};
}

function updateFields() {
	return { updatedAt: nowIso(), updatedBy: currentAuditUser() };
}

function verticalOf(verticalId: string | null | undefined) {
	const info = Object.values(MOCK_VERTICAL_INFO).find((vertical) => vertical.id === verticalId);
	return info ? { id: info.id, name: info.name } : KNOWLEDGE_BASE_VERTICAL;
}

function tagsByIds(tagIds: string[] | undefined): Tag[] {
	return (tagIds ?? [])
		.map((id) => state.tags.find((tag) => tag.id === id))
		.filter((tag): tag is Tag => tag !== undefined);
}

function orderedLegalRequestIds(dto: CreateThesisDTO | UpdateThesisDTO): string[] {
	return [...(dto.legalRequests?.items ?? [])]
		.sort((first, second) => first.order - second.order)
		.map((item) => item.legalRequestId);
}

function thesesUsing(predicate: (thesis: Thesis) => boolean) {
	return state.theses.filter(predicate).map(toThesisReference);
}

// ─── Hidratação ──────────────────────────────────────────────────────────────

function hydrateFact(fact: Fact, withTheses = false): Fact {
	const hydrated: Fact = { ...fact, tags: tagsByIds(fact.tags.map((tag) => tag.id)) };
	delete hydrated.theses;
	if (withTheses)
		hydrated.theses = thesesUsing((thesis) => thesis.facts.some((item) => item.id === fact.id));
	return hydrated;
}

function hydrateLegalRequest(legalRequest: LegalRequest, withTheses = false): LegalRequest {
	const hydrated: LegalRequest = {
		...legalRequest,
		isGlobal: isGlobalEntity(legalRequest),
		tags: tagsByIds(legalRequest.tags.map((tag) => tag.id)),
	};
	delete hydrated.theses;
	if (withTheses) {
		hydrated.theses = thesesUsing((thesis) =>
			(thesis.legalRequests ?? []).some((item) => item.id === legalRequest.id),
		);
	}
	return hydrated;
}

function hydrateThesis(thesis: Thesis): Thesis {
	return {
		...thesis,
		isGlobal: isGlobalEntity(thesis),
		tags: tagsByIds(thesis.tags.map((tag) => tag.id)),
		facts: thesis.facts
			.map((fact) => state.facts.find((item) => item.id === fact.id))
			.filter((fact): fact is Fact => fact !== undefined)
			.map((fact) => hydrateFact(fact)),
		legalRequests: (thesis.legalRequests ?? [])
			.map((legalRequest) => state.legalRequests.find((item) => item.id === legalRequest.id))
			.filter((legalRequest): legalRequest is LegalRequest => legalRequest !== undefined)
			.map((legalRequest) => hydrateLegalRequest(legalRequest)),
	};
}

function hydrateTopic(topic: Topic, withTheses = false): Topic {
	const hydrated: Topic = { ...topic, isGlobal: isGlobalEntity(topic) };
	delete hydrated.theses;
	if (withTheses)
		hydrated.theses = thesesUsing((thesis) => state.thesisTopicIds[thesis.id] === topic.id);
	return hydrated;
}

// ─── Rotas: fábrica CRUD ─────────────────────────────────────────────────────

interface CrudConfig<TEntity extends ScopedEntity, TCreate, TUpdate> {
	basePath: string;
	/** Substantivo com artigo, usado nas mensagens: "a tag", "o fato"… */
	noun: string;
	notFoundTitle: string;
	items: () => TEntity[];
	setItems: (items: TEntity[]) => void;
	/** Campos em que a busca (`search` ou `name`) procura */
	searchFields: (entity: TEntity) => (string | undefined)[];
	/** Filtro por tags (`tagIds`), para entidades que as possuem */
	tags?: (entity: TEntity) => Tag[];
	hydrate: (entity: TEntity, detailed: boolean) => TEntity;
	create: (body: TCreate, isAdmin: boolean) => TEntity;
	update: (entity: TEntity, body: TUpdate, isAdmin: boolean) => TEntity;
	/** Motivo que impede a exclusão (ex.: teses vinculadas), ou `null` */
	deleteBlocker?: (entity: TEntity) => string | null;
	onDelete?: (entity: TEntity) => void;
}

function crudRoutes<TEntity extends ScopedEntity, TCreate, TUpdate>(
	config: CrudConfig<TEntity, TCreate, TUpdate>,
): MockRoute[] {
	const { basePath } = config;

	const create = (isAdmin: boolean) => (request: MockRequest<TCreate>) => {
		const entity = config.create(request.body, isAdmin);
		config.setItems([entity, ...config.items()]);
		return config.hydrate(entity, true);
	};

	const update = (isAdmin: boolean) => (request: MockRequest<TUpdate>) => {
		const current = findOrFail(config.items(), request.params['id'], config.notFoundTitle);
		assertEditable(current, isAdmin, config.noun);
		const updated = config.update(current, request.body, isAdmin);
		config.setItems(config.items().map((item) => (item.id === updated.id ? updated : item)));
		return config.hydrate(updated, true);
	};

	const remove = (isAdmin: boolean) => (request: MockRequest) => {
		const current = findOrFail(config.items(), request.params['id'], config.notFoundTitle);
		assertEditable(current, isAdmin, config.noun);
		const blocker = config.deleteBlocker?.(current) ?? null;
		if (blocker) throw new MockHttpError(409, 'Não é possível excluir', blocker);
		config.setItems(config.items().filter((item) => item.id !== current.id));
		config.onDelete?.(current);
		return null;
	};

	return [
		mockRoute('GET', basePath, ({ query }) => {
			const term = query.get('search') ?? query.get('name');
			const filtered = config
				.items()
				.filter((entity) => matchesScope(entity, query))
				.filter((entity) => matchesTerm(config.searchFields(entity), term))
				.filter((entity) =>
					config.tags ? matchesTags({ tags: config.tags(entity) }, query) : true,
				);
			return paginate(
				sortEntities(filtered, query).map((entity) => config.hydrate(entity, false)),
				query,
			);
		}),
		mockRoute('GET', `${basePath}/:id`, ({ params }) =>
			config.hydrate(findOrFail(config.items(), params['id'], config.notFoundTitle), true),
		),
		mockRoute<TCreate>('POST', `${basePath}/admin`, create(true)),
		mockRoute<TCreate>('POST', basePath, create(false)),
		mockRoute<TUpdate>('PATCH', `${basePath}/admin/:id`, update(true)),
		mockRoute<TUpdate>('PATCH', `${basePath}/:id`, update(false)),
		mockRoute('DELETE', `${basePath}/admin/:id`, remove(true)),
		mockRoute('DELETE', `${basePath}/:id`, remove(false)),
	];
}

// ─── Tags ────────────────────────────────────────────────────────────────────

const TAG_ROUTES = crudRoutes<Tag, CreateTagDTO, UpdateTagDTO>({
	basePath: '/tags',
	noun: 'a tag',
	notFoundTitle: 'Tag não encontrada',
	items: () => state.tags,
	setItems: (items) => (state.tags = items),
	searchFields: (tag) => [tag.name, tag.slug],
	hydrate: (tag) => ({ ...tag }),
	create: (body, isAdmin) => {
		assertUniqueName(state.tags, body.name, isAdmin, 'Tag já cadastrada');
		return {
			id: newMockId('tag'),
			name: body.name.trim(),
			slug: uniqueSlug(body.name, state.tags),
			...creationFields(isAdmin),
		};
	},
	update: (tag, body, isAdmin) => {
		assertUniqueName(state.tags, body.name, isAdmin, 'Tag já cadastrada', tag.id);
		return {
			...tag,
			name: body.name.trim(),
			slug: uniqueSlug(body.name, state.tags, tag.id),
			...updateFields(),
		};
	},
	// Tag removida sai de fatos, pedidos e teses (a hidratação já ignora ids órfãos)
	onDelete: (tag) => {
		const withoutTag = <T extends { tags: Tag[] }>(item: T): T => ({
			...item,
			tags: item.tags.filter((candidate) => candidate.id !== tag.id),
		});
		state.facts = state.facts.map(withoutTag);
		state.legalRequests = state.legalRequests.map(withoutTag);
		state.theses = state.theses.map(withoutTag);
	},
});

// ─── Fatos ───────────────────────────────────────────────────────────────────

const FACT_ROUTES = crudRoutes<Fact, CreateFactDTO, UpdateFactDTO>({
	basePath: '/facts',
	noun: 'o fato',
	notFoundTitle: 'Fato não encontrado',
	items: () => state.facts,
	setItems: (items) => (state.facts = items),
	searchFields: (fact) => [fact.name, fact.slug],
	tags: (fact) => fact.tags,
	hydrate: hydrateFact,
	create: (body, isAdmin) => {
		assertUniqueName(state.facts, body.name, isAdmin, 'Fato já cadastrado');
		const vertical = verticalOf(body.verticalId);
		return {
			id: newMockId('fact'),
			slug: uniqueSlug(body.name, state.facts),
			name: body.name.trim(),
			description: body.description,
			classification: body.classification,
			schema: body.schema,
			examples: body.examples ?? [],
			tags: tagsByIds(body.tagIds),
			verticalId: vertical.id,
			vertical,
			...creationFields(isAdmin),
		};
	},
	// O slug é a variável usada nas regras e no conteúdo das teses: não muda na edição
	update: (fact, body, isAdmin) => {
		assertUniqueName(state.facts, body.name, isAdmin, 'Fato já cadastrado', fact.id);
		const vertical = body.verticalId !== undefined ? verticalOf(body.verticalId) : fact.vertical;
		return {
			...fact,
			name: body.name.trim(),
			description: body.description,
			classification: body.classification,
			schema: body.schema,
			examples: body.examples ?? [],
			tags: tagsByIds(body.tagIds),
			verticalId: vertical?.id ?? null,
			vertical: vertical ?? null,
			...updateFields(),
		};
	},
	deleteBlocker: (fact) =>
		state.theses.some((thesis) => thesis.facts.some((item) => item.id === fact.id))
			? 'Existem teses vinculadas a este fato. Remova o vínculo antes de excluí-lo.'
			: null,
});

// ─── Pedidos ─────────────────────────────────────────────────────────────────

const LEGAL_REQUEST_ROUTES = crudRoutes<LegalRequest, CreateLegalRequestDTO, UpdateLegalRequestDTO>(
	{
		basePath: '/legal-requests',
		noun: 'o pedido',
		notFoundTitle: 'Pedido não encontrado',
		items: () => state.legalRequests,
		setItems: (items) => (state.legalRequests = items),
		searchFields: (legalRequest) => [legalRequest.name, legalRequest.description],
		tags: (legalRequest) => legalRequest.tags,
		hydrate: hydrateLegalRequest,
		create: (body, isAdmin) => {
			assertUniqueName(state.legalRequests, body.name, isAdmin, 'Pedido já cadastrado');
			return {
				id: newMockId('lr'),
				name: body.name.trim(),
				description: body.description,
				tags: tagsByIds(body.tagIds),
				isGlobal: isAdmin,
				verticalId: body.verticalId ?? KNOWLEDGE_BASE_VERTICAL.id,
				...creationFields(isAdmin),
			};
		},
		update: (legalRequest, body, isAdmin) => {
			assertUniqueName(
				state.legalRequests,
				body.name,
				isAdmin,
				'Pedido já cadastrado',
				legalRequest.id,
			);
			return {
				...legalRequest,
				name: body.name.trim(),
				description: body.description,
				tags: tagsByIds(body.tagIds),
				...(body.verticalId !== undefined ? { verticalId: body.verticalId } : {}),
				...updateFields(),
			};
		},
		deleteBlocker: (legalRequest) =>
			state.theses.some((thesis) =>
				(thesis.legalRequests ?? []).some((item) => item.id === legalRequest.id),
			)
				? 'Existem teses vinculadas a este pedido. Remova o vínculo antes de excluí-lo.'
				: null,
	},
);

// ─── Teses ───────────────────────────────────────────────────────────────────

function thesisRelations(body: CreateThesisDTO | UpdateThesisDTO) {
	return {
		tags: tagsByIds(body.tagIds),
		facts: (body.factIds ?? [])
			.map((id) => state.facts.find((fact) => fact.id === id))
			.filter((fact): fact is Fact => fact !== undefined),
		legalRequests: orderedLegalRequestIds(body)
			.map((id) => state.legalRequests.find((legalRequest) => legalRequest.id === id))
			.filter((legalRequest): legalRequest is LegalRequest => legalRequest !== undefined),
	};
}

const THESIS_ROUTES = crudRoutes<Thesis, CreateThesisDTO, UpdateThesisDTO>({
	basePath: '/theses',
	noun: 'a tese',
	notFoundTitle: 'Tese não encontrada',
	items: () => state.theses,
	setItems: (items) => (state.theses = items),
	searchFields: (thesis) => [thesis.name, thesis.description],
	tags: (thesis) => thesis.tags,
	hydrate: hydrateThesis,
	create: (body, isAdmin) => {
		assertUniqueName(state.theses, body.name, isAdmin, 'Tese já cadastrada');
		return {
			id: newMockId('thesis'),
			slug: uniqueSlug(body.name, state.theses),
			name: body.name.trim(),
			description: body.description,
			tipoTeseProcessual: body.tipoTeseProcessual,
			content: body.content,
			ruleIncludes: body.ruleIncludes,
			...(body.ruleExcludes ? { ruleExcludes: body.ruleExcludes } : {}),
			isGlobal: isAdmin,
			verticalId: body.verticalId ?? KNOWLEDGE_BASE_VERTICAL.id,
			...thesisRelations(body),
			...creationFields(isAdmin),
		};
	},
	// Sem `ruleExcludes` no payload, a regra de exclusão foi removida
	update: (thesis, body, isAdmin) => {
		assertUniqueName(state.theses, body.name, isAdmin, 'Tese já cadastrada', thesis.id);
		const withoutExcludes: Thesis = { ...thesis };
		delete withoutExcludes.ruleExcludes;
		return {
			...withoutExcludes,
			name: body.name.trim(),
			description: body.description,
			tipoTeseProcessual: body.tipoTeseProcessual,
			content: body.content,
			ruleIncludes: body.ruleIncludes,
			...(body.ruleExcludes ? { ruleExcludes: body.ruleExcludes } : {}),
			...(body.verticalId !== undefined ? { verticalId: body.verticalId } : {}),
			...thesisRelations(body),
			...updateFields(),
		};
	},
	onDelete: (thesis) => {
		delete state.thesisTopicIds[thesis.id];
	},
});

// ─── Tópicos ─────────────────────────────────────────────────────────────────

const TOPIC_ROUTES = crudRoutes<Topic, CreateTopicDTO, UpdateTopicDTO>({
	basePath: '/topics',
	noun: 'o tópico',
	notFoundTitle: 'Tópico não encontrado',
	items: () => state.topics,
	setItems: (items) => (state.topics = items),
	searchFields: (topic) => [topic.name, topic.description],
	hydrate: hydrateTopic,
	create: (body, isAdmin) => {
		assertUniqueName(state.topics, body.name, isAdmin, 'Tópico já cadastrado');
		return {
			id: newMockId('topic'),
			name: body.name.trim(),
			description: body.description,
			enableBatchApproval: body.enableBatchApproval,
			isGlobal: isAdmin,
			...creationFields(isAdmin),
		};
	},
	update: (topic, body, isAdmin) => {
		assertUniqueName(state.topics, body.name, isAdmin, 'Tópico já cadastrado', topic.id);
		return {
			...topic,
			name: body.name.trim(),
			description: body.description,
			enableBatchApproval: body.enableBatchApproval,
			...updateFields(),
		};
	},
	deleteBlocker: (topic) =>
		Object.values(state.thesisTopicIds).includes(topic.id)
			? 'Existem teses vinculadas a este tópico. Remova o vínculo antes de excluí-lo.'
			: null,
});

export const KNOWLEDGE_BASE_MOCK_ROUTES: MockRoute[] = [
	...TAG_ROUTES,
	...FACT_ROUTES,
	...LEGAL_REQUEST_ROUTES,
	...THESIS_ROUTES,
	...TOPIC_ROUTES,
];
